import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { RootState } from 'state/store';
import { selectCapacityFilters } from 'state/Capacities/Filters/filtersSlice';
import { fetchTeamMemberProjectsList } from 'state/Capacities/TeamMemberProjectsList/teamMemberProjectsListSlice';
import {
  ProjectsByTeamMember,
  TeamMemberProjects,
} from 'utils/types/dailyTeamsCapacity';
import useCapacityCancellationContext from 'Pages/TeamsPage/views/TeamsCapacity/components/CapacityTable/context/useCapacityCancellationContext';

const BATCH_SIZE = 6;

const useRefetchProjectsListOnFiltersChange = (): void => {
  const dispatch = useDispatch();
  const { getCancelToken, cancelInFlightRequests } =
    useCapacityCancellationContext();
  const filters = useSelector(selectCapacityFilters);
  const expandedMembers = useSelector(
    (state: RootState) => state.capacities.teamMemberProjectsList.byTeamMember
  );

  const prevFiltersRef = useRef(filters);
  const filterVersionRef = useRef(0);
  const expandedMembersRef = useRef<ProjectsByTeamMember<TeamMemberProjects>>(
    {}
  );
  expandedMembersRef.current = expandedMembers;

  useEffect(() => {
    if (isEqual(prevFiltersRef.current, filters)) {
      return;
    }

    prevFiltersRef.current = filters;
    filterVersionRef.current++;
    cancelInFlightRequests();
    const currentVersion = filterVersionRef.current;

    const refetch = async () => {
      try {
        const requests: Array<{ teamId: string; memberId: string }> = [];
        for (const teamId of Object.keys(expandedMembersRef.current)) {
          for (const memberId of Object.keys(
            expandedMembersRef.current[teamId] || {}
          )) {
            if (memberId === 'allProjectsIds') continue;
            requests.push({ teamId, memberId });
          }
        }

        for (let i = 0; i < requests.length; i += BATCH_SIZE) {
          if (currentVersion !== filterVersionRef.current) return;
          const batch = requests.slice(i, i + BATCH_SIZE);
          const batchPromises = batch.map(({ teamId, memberId }) =>
            dispatch(
              fetchTeamMemberProjectsList({
                teamId,
                memberId,
                force: true,
                cancelToken: getCancelToken(),
              }) as any
            ).unwrap()
          );

          await Promise.all(batchPromises);
        }
      } catch {
        // cancelled or rejected — chain stops
      }
    };

    refetch();
  }, [filters, dispatch, getCancelToken, cancelInFlightRequests]);
};

export default useRefetchProjectsListOnFiltersChange;
